import React, { useEffect } from "react";

import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import { useIframeMessageRouter } from "@agent-ui/features/modifiers/hooks/useIframeMessageRouter";
import { useIframeMessaging } from "@agent-ui/features/modifiers/hooks/useIframeMessaging";
import {
  useDisplayMode,
  useIsIframeLoaded,
  useModifiersStoreActions,
  usePendingPreviews,
  useSemanticElement,
} from "@agent-ui/features/modifiers/store/ModifiersStoreContext";
import { usePage } from "@agent-ui/features/page-renderer/hooks/usePage";
import {
  useEditorDraftStyleLibrary,
  useViewingVersionId,
} from "@agent-ui/store/editorStore";
import { useOnValueChange } from "replo-utils/react/use-on-value-change";

import PageRenderer from "./PageRenderer";

type ModifiablePageRendererProps = {
  pageRoute: string;
  className?: string;
  isInteractive?: boolean;
  showControls?: boolean;
  previewThumbnail?: boolean;
};

/**
 * A wrapper around PageRenderer that adds modifier functionality.
 * This component should only be used when modifier interactions are needed.
 */
function ModifiablePageRenderer({
  pageRoute,
  className,
  showControls = true,
  isInteractive = false,
  previewThumbnail,
}: ModifiablePageRendererProps) {
  const displayMode = useDisplayMode();
  const isIframeLoaded = useIsIframeLoaded();
  const modifierActions = useModifiersStoreActions();
  const draftStyleLibrary = useEditorDraftStyleLibrary();
  const viewingVersionId = useViewingVersionId();
  const pendingPreviews = usePendingPreviews();
  const sessionUuid = useSessionUuid() ?? undefined;

  // Note (Nick, 2025-12-02): Get iframeKey to detect when iframe remounts (sandbox refresh)
  const { iframeKey: currentIframeKey } = usePage({
    pageRoute,
    overrideStyleLibrary: draftStyleLibrary,
    viewingVersionId,
    forceSkipSandboxSync: !isInteractive,
  });

  const semanticElement = useSemanticElement();

  const hoverRef = React.useRef<HTMLDivElement>(null);
  // NOTE (Yuxin, 2026-02-02): Ref for semantic element selection overlay
  const semanticOverlayRef = React.useRef<HTMLDivElement>(null);
  // NOTE (Yuxin, 2026-02-03): Ref for semantic element tag positioning
  const semanticTagRef = React.useRef<HTMLDivElement>(null);

  const iframeContainerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      modifierActions.setIframeContainer(node);
    },
    [modifierActions],
  );

  const { sendMessage } = useIframeMessaging();

  // Reapply any pending previews after iframe refresh (prevents flicker while LLM persists)
  useOnValueChange(currentIframeKey, () => {
    if (!isIframeLoaded || pendingPreviews.length === 0) {
      return;
    }

    const previewsToApply = pendingPreviews.filter((preview) => {
      return (
        (!preview.pageRoute || preview.pageRoute === pageRoute) &&
        (!sessionUuid ||
          !preview.sessionUuid ||
          preview.sessionUuid === sessionUuid)
      );
    });

    previewsToApply.forEach((preview) => {
      sendMessage({
        type: "APPLY_PREVIEW",
        payload: {
          selector: preview.selector,
          newHtml: preview.newHtml,
          containerOpeningTag: preview.containerOpeningTag,
        },
      });
    });
  });

  const latestRectRef = React.useRef<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const groupedElementRectsRef = React.useRef<
    { top: number; left: number; width: number; height: number }[]
  >([]);

  /**
   * NOTE (Nick, 2025-12-01): Resets modifier state when navigating between pages.
   *
   * When the user navigates to a different page, we reset to interactive mode
   * and clear any selected elements. This ensures clean state on each page and
   * prevents stale element references from the previous page.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: effect intentionally runs on pageRoute change
  useEffect(() => {
    modifierActions.resetToDefaults();
  }, [pageRoute, modifierActions]);

  /**
   * NOTE (Kurt, 2025-08-04): Synchronizes display mode between editor and preview iframe.
   *
   * When the editor switches between interactive and select modes, this effect
   * notifies the iframe so it can adjust its behavior accordingly. In select mode,
   * the iframe intercepts clicks for element selection; in interactive mode, it
   * allows normal user interactions.
   *
   * NOTE (Nick, 2025-12-01): Added pageRoute dependency to ensure the iframe
   * receives the current display mode after page navigation, keeping editor and
   * iframe synchronized.
   *
   * NOTE (Nick, 2025-12-04): Added isIframeLoaded dependency so this effect re-runs
   * when iframe becomes loaded, ensuring the current displayMode is sent after handshake.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: effect intentionally runs on pageRoute change
  useEffect(() => {
    sendMessage({
      type: "SET_DISPLAY_MODE",
      payload: { displayMode },
    });
  }, [displayMode, pageRoute, isIframeLoaded, sendMessage]);

  useOnValueChange(currentIframeKey, () => {
    modifierActions.resetToDefaults();

    latestRectRef.current = null;
    if (hoverRef.current) {
      hoverRef.current.style.display = "none";
    }

    sendMessage({ type: "CLEAR_SELECTION" });
    sendMessage({
      type: "SET_DISPLAY_MODE",
      payload: { displayMode },
    });
  });

  /** Centralized router for all iframe-to-parent window messages. */
  useIframeMessageRouter({
    showControls,
    hoverRef,
    latestRectRef,
    groupedElementRectsRef,
  });

  /**
   * NOTE (Kurt, 2025-08-04): Continuously updates overlay positioning using requestAnimationFrame.
   *
   * This effect runs every frame to ensure the red border (selected element) and text edit
   * toolbar stay positioned correctly over the selected element. It reads from latestRectRef
   * (updated by message events) and applies smooth visual positioning to the overlay elements.
   *
   * Uses requestAnimationFrame for smooth 60fps updates, essential for responsive visual feedback.
   */
  useEffect(() => {
    if (!showControls) {
      return;
    }

    let animationFrameId: number;

    const updateOverlay = () => {
      const rect = latestRectRef.current;

      if (rect) {
        const { top, left, width, height } = rect;

        // NOTE (Yuxin, 2026-02-03): Update semantic overlay position from latestRectRef
        // This ensures the bounding box stays in sync during scroll/resize
        if (semanticOverlayRef.current && semanticElement) {
          Object.assign(semanticOverlayRef.current.style, {
            top: `${top}px`,
            left: `${left}px`,
            width: `${width}px`,
            height: `${height}px`,
            display: "block",
          });
        }

        // Update semantic tag position
        if (semanticTagRef.current && semanticElement) {
          Object.assign(semanticTagRef.current.style, {
            top: `${top - 30}px`,
            left: `${left}px`,
            display: "block",
          });
        }
      }

      animationFrameId = requestAnimationFrame(updateOverlay);
    };

    animationFrameId = requestAnimationFrame(updateOverlay);

    return () => cancelAnimationFrame(animationFrameId);
    /**
     * TODO (Kurt, 2025-08-05): Consider using useBoundingBox() hook instead of
     * manually managing position state with requestAnimationFrame. The useBoundingBox
     * hook provides automatic position tracking with mutation observers and optimized
     * updates. Not implementing now to avoid disrupting current positioning logic.
     */
  }, [showControls, semanticElement]);

  // Create overlay elements when showControls is true
  const overlayElements = showControls ? (
    <>
      <div
        ref={hoverRef}
        className="absolute border-2 border-dashed border-primary pointer-events-none"
        style={{
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          display: "none",
        }}
      />
      {semanticElement && (
        <>
          <div
            ref={semanticOverlayRef}
            className="absolute border-2 border-primary pointer-events-none"
            style={{
              top: `${semanticElement.boundingBox.y}px`,
              left: `${semanticElement.boundingBox.x}px`,
              width: `${semanticElement.boundingBox.width}px`,
              height: `${semanticElement.boundingBox.height}px`,
              display: "block",
            }}
          />
          <div
            ref={semanticTagRef}
            className="absolute pointer-events-none z-20"
            style={{
              top: `${semanticElement.boundingBox.y - 30}px`,
              left: `${semanticElement.boundingBox.x}px`,
              display: "block",
            }}
          >
            <div className="bg-primary text-white text-xs px-2 py-1 rounded-t-md font-medium capitalize">
              {semanticElement.elementType}
            </div>
          </div>
        </>
      )}
    </>
  ) : undefined;

  return (
    <PageRenderer
      pageRoute={pageRoute}
      className={className}
      isInteractive={isInteractive}
      previewThumbnail={previewThumbnail}
      containerRef={iframeContainerRef}
      overlayElements={overlayElements}
    />
  );
}

export default ModifiablePageRenderer;
